import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, DmsTextField, intl, fetchData } from "dmsbasereact";
import {
    Box,
    useMediaQuery,
    useTheme,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Chip,
    Typography,
    CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";

const DirectorGroupFormModal = ({
    isVisible,
    onClose,
    onSave = null,
    group = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isEditMode = Boolean(group);

    const [formName, setFormName] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formMembers, setFormMembers] = useState([]);
    const [availableDirectors, setAvailableDirectors] = useState([]);
    const [loadingDirectors, setLoadingDirectors] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAvailableDirectors = useCallback(async () => {
        setLoadingDirectors(true);
        try {
            const response = await fetchData("/director_group/available-directors", "GET", true);
            const data = response?.data?.data || response?.data || [];
            setAvailableDirectors(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load available directors:", error);
            setAvailableDirectors([]);
        } finally {
            setLoadingDirectors(false);
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            loadAvailableDirectors();
            if (group) {
                setFormName(group.name || "");
                setFormDescription(group.description || "");
                setFormMembers(group.members?.map((m) => m.dms_user_pk) || []);
            } else {
                setFormName("");
                setFormDescription("");
                setFormMembers([]);
            }
        }
    }, [isVisible, group, loadAvailableDirectors]);

    const handleSave = async () => {
        if (!formName.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("GroupNameRequired").d("Group name is required."),
            });
            return;
        }

        const confirmResult = await Swal.fire({
            icon: "question",
            title: intl.get("ConfirmAction").d("Confirm Action"),
            text: isEditMode
                ? intl.get("ConfirmUpdateGroup").d("Are you sure you want to update this group?")
                : intl.get("ConfirmCreateGroup").d("Are you sure you want to create this group?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: formName.trim(),
                description: formDescription.trim() || null,
                members: formMembers.map((pk) => ({ dms_user_pk: pk })),
            };

            let response;
            if (isEditMode) {
                response = await fetchData(`/director_group/${group.pk}`, "PUT", true, payload);
            } else {
                response = await fetchData("/director_group", "POST", true, payload);
            }

            if (response.status >= 200 && response.status < 300) {
                await Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: isEditMode
                        ? intl.get("GroupUpdatedSuccessfully").d("Group updated successfully")
                        : intl.get("GroupCreatedSuccessfully").d("Group created successfully"),
                });
                if (onSave) onSave();
                onClose();
            } else {
                const errorMessage = response.data?.detail || intl.get("FailedToSaveGroup").d("Failed to save group");
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: errorMessage,
                });
            }
        } catch (error) {
            console.error("Failed to save group:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToSaveGroup").d("Failed to save group"),
            });
        } finally {
            setSaving(false);
        }
    };

    const title = isEditMode
        ? intl.get("EditDirectorGroup").d("Edit Director Group")
        : intl.get("CreateDirectorGroup").d("Create Director Group");

    const isFormValid = formName.trim().length > 0;

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={title} onClose={onClose} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("BasicInfo").d("Basic Information")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="name"
                                label={intl.get("GroupName").d("Group Name")}
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="description"
                                label={intl.get("Description").d("Description")}
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("Members").d("Members")}
                    </Typography>
                    <FormControl fullWidth disabled={loadingDirectors}>
                        <InputLabel>{intl.get("SelectMembers").d("Select Members")}</InputLabel>
                        <Select
                            multiple
                            value={formMembers}
                            label={intl.get("SelectMembers").d("Select Members")}
                            onChange={(e) => setFormMembers(e.target.value)}
                            input={<OutlinedInput label={intl.get("SelectMembers").d("Select Members")} />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((pk) => {
                                        const director = availableDirectors.find((d) => d.pk === pk);
                                        return (
                                            <Chip
                                                key={pk}
                                                label={director?.name || pk}
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {loadingDirectors ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    {intl.get("Loading").d("Loading...")}
                                </MenuItem>
                            ) : (
                                availableDirectors.map((director) => (
                                    <MenuItem key={director.pk} value={director.pk}>
                                        <Checkbox checked={formMembers.indexOf(director.pk) > -1} />
                                        <ListItemText
                                            primary={director.name}
                                            secondary={director.email}
                                        />
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={onClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={!isFormValid || saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

DirectorGroupFormModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    group: PropTypes.object,
};

export { DirectorGroupFormModal };
